import { Routes } from '@angular/router';
import { authGuard } from '@core/guards/auth.guard';
import { featureGuard } from '@core/guards/feature.guard';
import { menuAccessChildGuard } from '@core/guards/menu-access.guard';
import { adminGuard } from '@core/guards/admin.guard';
import { pricingAccessGuard } from '@core/guards/pricing-access.guard';
import {
  subscriptionAccessChildGuard,
  subscriptionAccessGuard,
} from '@core/guards/subscription-access.guard';
import { LayoutComponent } from '@shared/layout/layout.component';
import { HomeComponent } from '@modules/home/home.component';

export const routes: Routes = [
  {
    path: 'pricing',
    canActivate: [pricingAccessGuard],
    loadComponent: () =>
      import(
        '@shared/pages/subscription-pricing/subscription-pricing.component'
      ).then((m) => m.SubscriptionPricingComponent),
  },
  {
    path: 'activate-account',
    loadComponent: () =>
      import('@modules/auth/activate-account/activate-account.component').then(
        (m) => m.ActivateAccountComponent,
      ),
  },
  {
    path: 'subscription-access',
    redirectTo: 'pricing',
    pathMatch: 'full',
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [authGuard, subscriptionAccessGuard],
    canActivateChild: [subscriptionAccessChildGuard, menuAccessChildGuard],
    children: [
      {
        path: '',
        component: HomeComponent,
      },
      {
        path: 'users',
        canActivate: [featureGuard],
        data: {
          feature: 'users.manage',
          redirectTo: '/my-account',
          blockedMessage: 'Tu plan solo permite un usuario. Actualiza para agregar empleados.',
        },
        loadComponent: () => import('@modules/user/user.component').then(m => m.UserComponent),
      },
      {
        path: 'role',
        canActivate: [featureGuard],
        data: {
          feature: 'users.roles_permissions',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para gestionar roles y permisos.',
        },
        loadComponent: () => import('@modules/role/role.component').then(m => m.RoleComponent),
      },
      {
        path: 'menu',
        loadChildren: () => import('@modules/menu/menu.routes').then(m => m.MENU_ROUTES),
      },
      {
        path: 'permissions',
        canActivate: [featureGuard],
        data: {
          feature: 'users.roles_permissions',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para gestionar roles y permisos.',
        },
        loadComponent: () => import('@modules/permission/permission.component').then(m => m.PermissionComponent),
      },
      {
        path: 'my-account',
        loadComponent: () =>
          import('@modules/my-account/my-account.component').then(
            (m) => m.MyAccountComponent,
          ),
      },
      {
        path: 'my-tasks',
        canActivate: [featureGuard],
        data: {
          feature: 'employees.attendance',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para acceder a la gestión de actividades.',
        },
        loadComponent: () =>
          import('@modules/employee/my-tasks.component').then(
            (m) => m.MyTasksComponent,
          ),
      },
      {
        path: 'my-attendance',
        canActivate: [featureGuard],
        data: {
          feature: 'employees.attendance',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para acceder al control de asistencia.',
        },
        loadComponent: () =>
          import('@modules/employee/my-attendance/my-attendance.component').then(
            (m) => m.MyAttendanceComponent,
          ),
      },
      {
        path: 'admin/task-manager',
        canActivate: [featureGuard],
        data: {
          feature: 'employees.attendance',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para acceder al gestor de tareas.',
        },
        loadComponent: () =>
          import('@modules/employee/task-manager.component').then(
            (m) => m.TaskManagerComponent,
          ),
      },
      {
        path: 'admin/attendance',
        canActivate: [featureGuard],
        data: {
          feature: 'employees.attendance',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para acceder al reporte de asistencia.',
        },
        loadComponent: () =>
          import('@modules/employee/admin-attendance.component').then(
            (m) => m.AdminAttendanceComponent,
          ),
      },
      {
        path: 'categories',
        loadComponent: () => import('@modules/category/category.component').then(m => m.CategoryComponent),
      },
      {
        path: 'company',
        loadComponent: () => import('@modules/company/company.component').then(m => m.CompanyComponent),
      },
      {
        path: 'branches',
        canActivate: [featureGuard],
        data: {
          feature: 'branches.module',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para gestionar múltiples sucursales.',
        },
        loadComponent: () => import('@modules/branches/branches.component').then(m => m.BranchesComponent),
      },
      {
        path: 'products',
        loadChildren: () => import('@modules/product/product.routes').then(m => m.productRoutes),
      },
      {
        path: 'ingredients',
        canActivate: [featureGuard],
        data: {
          feature: 'ingredients.module',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para acceder al módulo de insumos.',
        },
        loadChildren: () =>
          import('@modules/ingredients/ingredients.routes').then(
            (m) => m.ingredientRoutes,
          ),
      },
      {
        path: 'customers',
        canActivate: [featureGuard],
        data: {
          feature: 'contacts.customers',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para acceder a clientes.',
        },
        loadComponent: () =>
          import('@modules/customer/customer.component').then(
            (m) => m.CustomerComponent,
          ),
      },
      {
        path: 'rewards',
        canActivate: [featureGuard, adminGuard],
        data: {
          feature: 'marketing.rewards',
          redirectTo: '/pricing',
          blockedMessage: 'El programa de recompensas está disponible a partir del plan Profesional.',
        },
        loadComponent: () =>
          import('@modules/rewards/rewards.component').then(
            (m) => m.RewardsComponent,
          ),
      },
      {
        path: 'memberships',
        canActivate: [featureGuard],
        data: {
          feature: 'marketing.memberships',
          redirectTo: '/pricing',
          blockedMessage: 'El módulo de membresías está disponible a partir del plan Profesional.',
        },
        loadComponent: () =>
          import('@modules/memberships/memberships.component').then(
            (m) => m.MembershipsComponent,
          ),
      },
      {
        path: 'offers',
        canActivate: [featureGuard, adminGuard],
        data: {
          feature: 'marketing.offers',
          redirectTo: '/pricing',
          blockedMessage: 'Las ofertas express están disponibles a partir del plan Profesional.',
        },
        loadComponent: () =>
          import('@modules/offers/offers.component').then(
            (m) => m.OffersComponent,
          ),
      },
      {
        path: 'suppliers',
        canActivate: [featureGuard],
        data: {
          feature: 'contacts.suppliers',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para acceder a proveedores.',
        },
        loadComponent: () =>
          import('@modules/supplier/supplier.component').then(
            (m) => m.SupplierComponent,
          ),
      },
      {
        path: 'customers',
        canActivate: [featureGuard],
        data: {
          feature: 'contacts.customers',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para acceder a clientes y recompensas.',
        },
        loadComponent: () =>
          import('@modules/customer/customer.component').then(
            (m) => m.CustomerComponent,
          ),
      },
      {
        path: 'inventory',
        loadChildren: () => import('@modules/inventory/inventory.routes').then(m => m.inventoryRoutes),
      },
      {
        path: 'inventory-movements',
        canActivate: [featureGuard],
        data: {
          feature: 'inventory.movements',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para acceder a la gestión de entradas y salidas.',
        },
        loadChildren: () => import('@modules/inventory-movements/inventory-movements.routes').then(m => m.inventoryMovementsRoutes),
      },
      {
        path: 'cash',
        loadChildren: () =>
          import('@modules/cash/cash.routes').then(
            (m) => m.cashRoutes,
          ),
      },
      {
        path: 'brand',
        loadChildren: () => import('@modules/brand/brand.routes').then(m => m.brandRoutes),
      },
      {
        path: 'tax-profile',
        loadComponent: () =>
          import('@modules/tax-profile/tax-profile.component').then(
            (m) => m.TaxProfileComponent,
          ),
      },
      {
        path: 'sale',
        loadChildren: () => import('@modules/sale/sale.routes').then(m => m.saleRoutes),
      },
      {
        path: 'sale-detail',
        loadChildren: () =>
          import('@modules/sale-detail/sale-detail.routes').then(
            (m) => m.saleDetailRoutes,
          ),
      },
      {
        path: 'returns',
        canActivate: [featureGuard],
        data: {
          feature: 'sales.returns',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para acceder al módulo de devoluciones.',
        },
        loadComponent: () =>
          import('@modules/returns/returns.component').then(
            (m) => m.ReturnsComponent,
          ),
      },
      {
        path: 'devices',
        canActivate: [featureGuard],
        data: {
          feature: 'devices.module',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para acceder al módulo de dispositivos.',
        },
        loadChildren: () =>
          import('@modules/devices/devices.routes').then(
            (m) => m.devicesRoutes,
          ),
      },
      {
        path: 'finances',
        canActivate: [featureGuard],
        data: {
          feature: 'finance.expenses',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para acceder al módulo de finanzas.',
        },
        loadChildren: () =>
          import('@modules/expenses/expenses.routes').then(
            (m) => m.expensesRoutes,
          ),
      },
      {
        path: 'profit-loss',
        canActivate: [featureGuard, adminGuard],
        data: {
          feature: 'finance.profit_loss',
          redirectTo: '/pricing',
          blockedMessage: 'Actualiza tu plan para acceder a la gestión de ganancias.',
        },
        loadComponent: () =>
          import('@modules/profit-loss/profit-loss.component').then(
            (m) => m.ProfitLossComponent,
          ),
      },
      {
        path: 'reports',
        canActivate: [featureGuard, adminGuard],
        data: {
          feature: 'reports.sales_summary',
          redirectTo: '/pricing',
          blockedMessage: 'Los reportes están disponibles a partir del plan Emprendedor.',
        },
        loadComponent: () =>
          import('@modules/reports/reports.component').then(
            (m) => m.ReportsComponent,
          ),
      },

    ]
  },
  {
    path: 'payment-terminals/mercado-pago/callback',
    canActivate: [authGuard, subscriptionAccessGuard, featureGuard],
    data: {
      feature: 'devices.module',
      redirectTo: '/pricing',
      blockedMessage: 'Actualiza tu plan para acceder al módulo de dispositivos.',
    },
    loadComponent: () =>
      import('@modules/terminal-payment/providers/mercado-pago/mercado-pago-callback.component').then(
        (m) => m.MercadoPagoCallbackComponent,
      ),
  },
  { path: 'reset-password', loadComponent: () => import('@modules/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent) },
  { path: 'register', loadComponent: () => import('@modules/auth/register/register.component') },
  { path: 'login', loadComponent: () => import('@modules/auth/login/login.component') },
  {
    path: '404',
    loadComponent: () =>
      import('@shared/pages/not-found/not-found.component').then(
        (m) => m.NotFoundComponent,
      ),
  },
  { path: '**', redirectTo: '404', pathMatch: 'full' },
];
